import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import clamp from 'lodash/clamp';
import type { VirtualGridOverlayApiT } from '../../../common/virtual-grid';
import { dispatchAtom } from '../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../atoms/label-store-dispatch/events';
import { MARKER_CURSOR, TIMELINE_EVENT } from '../constants/frame-timeline-v4';
import { useTimelineGestures } from '../providers/timeline-gestures';
import type { MarkerDragRefsT, TimelineGestureDetailT } from '../types/frame-timeline-v4';
import { columnIndexToFrame } from '../utils/frame-timeline-v4';

/**
 * Lets the user grab the current-frame marker's badge and scrub the frame directly.
 * The marker itself renders declaratively off `currentFrameAtom`, same as any other
 * consumer — this hook's only job is to resolve the frame under the pointer and dispatch
 * `SET_CURRENT_FRAME` on every frame actually crossed (not on every pointermove, which is
 * quantized away by the frame-vs-column resolution below). Autoscroll and the marker's own
 * position/label both react to that dispatch the same way they react to any other source of
 * frame changes (playback, span edits) — there's nothing drag-specific left for them to know.
 */
export function useFrameMarkerDrag(overlayControls: VirtualGridOverlayApiT, totalFrames: number, {
  badgeRef
}: MarkerDragRefsT) {
  const {
    stage
  } = useTimelineGestures();
  const dispatch = useSetAtom(dispatchAtom);
  const dragRef = useRef<{
    frame: number;
  } | null>(null);
  const resolveFrame = useCallback((clientX: number) => {
    /* The pointer's `event.detail.frame` (from the shared gesture bus's DOM hit-test)
     is deliberately ignored here: it resolves via `elementFromPoint(...).closest('[data-frame]')`,
     but the pointer sits over the marker's own badge/line during this drag, not a
     `[data-frame]` cell, so that hit-test would return null for the whole gesture.
     `columnIndexAt(clientX)` resolves position independent of what's under the pointer. */
    const columnIndex = overlayControls.columnIndexAt(clientX);
    if (columnIndex === null) {
      return null;
    }
    // Frames occupy columns 1..totalFrames, so the last valid frame is totalFrames - 1.
    return clamp(columnIndexToFrame(columnIndex), 0, totalFrames - 1);
  }, [overlayControls, totalFrames]);
  const commitFrame = useCallback((frame: number) => {
    dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId: frame
    });
  }, [dispatch]);
  const onDragStart = useCallback((event: CustomEvent<TimelineGestureDetailT>) => {
    if (event.detail.target !== badgeRef.current) {
      return;
    }
    const frame = resolveFrame(event.detail.clientX);
    if (frame === null) {
      return;
    }
    dragRef.current = {
      frame
    };
    if (badgeRef.current) {
      badgeRef.current.style.cursor = MARKER_CURSOR.DRAGGING;
    }
    commitFrame(frame);
  }, [badgeRef, commitFrame, resolveFrame]);
  const onDrag = useCallback((event: CustomEvent<TimelineGestureDetailT>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const frame = resolveFrame(event.detail.clientX);
    if (frame === null || frame === drag.frame) {
      return;
    }
    drag.frame = frame;
    commitFrame(frame);
  }, [commitFrame, resolveFrame]);
  const onDragEnd = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (badgeRef.current) {
      badgeRef.current.style.cursor = MARKER_CURSOR.IDLE;
    }
    if (!drag) {
      return;
    }
    // Safety-net final commit: guarantees the release point is what's committed even if,
    // for some reason, the last DRAG event that reached this frame didn't (it always
    // should have, but this makes the guarantee independent of that).
    commitFrame(drag.frame);
  }, [badgeRef, commitFrame]);
  useEffect(() => {
    stage.addEventListener(TIMELINE_EVENT.DRAG_START, onDragStart);
    stage.addEventListener(TIMELINE_EVENT.DRAG, onDrag);
    stage.addEventListener(TIMELINE_EVENT.DRAG_END, onDragEnd);
    return () => {
      stage.removeEventListener(TIMELINE_EVENT.DRAG_START, onDragStart);
      stage.removeEventListener(TIMELINE_EVENT.DRAG, onDrag);
      stage.removeEventListener(TIMELINE_EVENT.DRAG_END, onDragEnd);
    };
  }, [stage, onDragStart, onDrag, onDragEnd]);
}